import {
  Accordion,
  ActionIcon,
  Alert,
  Box,
  Button,
  Card,
  Collapse,
  Grid,
  Group,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { useTranslation } from '@refinedev/core';
import type { UseQueryResult } from '@tanstack/react-query';
import type { Comment } from '@billing/validation/comment';
import { IconChevronDown, IconChevronUp, IconNotes } from '@tabler/icons-react';
import { useId } from 'react';
import { ClaimCommentHistory } from './ClaimCommentHistory';
import { Section, SectionProps } from './Section';
import { ModalTextArea } from 'components/common/ModalComponents/ModalTextArea';

export const ClaimCommentSection = ({
  index,
  title,
  keys,
  cols,
  editComponentsMap,
  type = 'edit',
  claimID,
  commentsQuery,
  timezone,
  onSaveComment,
  canSaveComment = false,
  canDeleteComment = false,
  isSavingComment = false,
  isCommentSaveError = false,
  collapsible = false,
  expanded = true,
  onExpandedChange,
  description,
  compact = false,
}: SectionProps & {
  claimID?: string;
  commentsQuery?: UseQueryResult<Comment[], Error>;
  timezone?: string;
  onSaveComment?: () => Promise<void>;
  canSaveComment?: boolean;
  canDeleteComment?: boolean;
  isSavingComment?: boolean;
  isCommentSaveError?: boolean;
  collapsible?: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  description?: string;
  compact?: boolean;
}) => {
  const { translate } = useTranslation();
  const contentID = useId();

  if (compact) {
    return (
      <Card
        padding="lg"
        shadow="xs"
        radius="sm"
        withBorder
        data-testid="claim-note-section"
      >
        <Group justify="space-between" wrap="nowrap">
          <UnstyledButton
            onClick={() => onExpandedChange?.(!expanded)}
            aria-expanded={expanded}
            aria-controls={contentID}
            style={{ flex: 1, minWidth: 0 }}
          >
            <Group gap="xs" wrap="nowrap">
              <IconNotes size={20} />
              <Text fw={600}>{title}</Text>
            </Group>
          </UnstyledButton>
          <ActionIcon
            type="button"
            size="input-sm"
            variant="subtle"
            color="gray"
            radius="sm"
            aria-label={translate(
              expanded
                ? 'pages:claims.comments.collapse'
                : 'pages:claims.comments.expand',
              expanded
                ? 'Collapse internal comments'
                : 'Expand internal comments'
            )}
            aria-expanded={expanded}
            aria-controls={contentID}
            onClick={() => onExpandedChange?.(!expanded)}
          >
            {expanded ? (
              <IconChevronUp size={18} />
            ) : (
              <IconChevronDown size={18} />
            )}
          </ActionIcon>
        </Group>
        <Collapse in={expanded} id={contentID}>
          <Stack gap="lg" mt="md">
            {commentsQuery && (
              <ClaimCommentHistory
                commentsQuery={commentsQuery}
                claimID={claimID}
                canDeleteComment={canDeleteComment}
                timezone={timezone ?? 'UTC'}
                compact
              />
            )}
            {description && (
              <Text size="sm" c="dimmed">
                {description}
              </Text>
            )}
            <Box w="100%" maw={650}>
              <Stack gap="xs">
                <ModalTextArea
                  labelFontSize={14}
                  label={translate(
                    type === 'create'
                      ? 'pages:claims.comments.newComment'
                      : 'pages:claims.comments.addLabel',
                    type === 'create' ? 'New comment' : 'Add a comment'
                  )}
                  placeholder={translate(
                    'pages:claims.comments.placeholder',
                    'Write a comment'
                  )}
                  accessorKey="internalComments"
                  maxLength={500}
                />
                {isCommentSaveError && (
                  <Alert color="red" role="alert">
                    {translate(
                      'pages:claims.comments.saveError',
                      'The comment could not be added. Please try again.'
                    )}
                  </Alert>
                )}
                {claimID && onSaveComment && (
                  <Group justify="flex-start">
                    <Button
                      variant="outline"
                      size="sm"
                      color="gray"
                      c={
                        canSaveComment ? 'var(--mantine-color-text)' : undefined
                      }
                      onClick={onSaveComment}
                      loading={isSavingComment}
                      disabled={!canSaveComment}
                    >
                      {translate('pages:claims.comments.add', 'Add comment')}
                    </Button>
                  </Group>
                )}
              </Stack>
            </Box>
          </Stack>
        </Collapse>
      </Card>
    );
  }

  const content = (
    <Section
      index={index}
      title={title}
      icon={<IconNotes size={20} />}
      keys={keys}
      cols={cols}
      grow
      editComponentsMap={editComponentsMap}
      type={type}
      hideLabel={collapsible}
    >
      <Stack w="100%" gap="sm">
        {description && (
          <Text size="sm" c="dimmed">
            {description}
          </Text>
        )}
        <Grid
          gutter={{ base: 'xs', sm: 'sm', lg: 'lg' }}
          w="100%"
          justify="space-around"
          grow
        >
          {commentsQuery && (
            <Grid.Col span={12}>
              <ClaimCommentHistory
                commentsQuery={commentsQuery}
                claimID={claimID}
                canDeleteComment={canDeleteComment}
                timezone={timezone ?? 'UTC'}
              />
            </Grid.Col>
          )}
          {keys.map((key) => (
            <Grid.Col
              key={key}
              span={{ base: 12, sm: 'content', lg: 12 / cols }}
            >
              {type === 'edit' ? (
                editComponentsMap[`${index}_${key}`]
              ) : (
                <ModalTextArea
                  label={translate(
                    'pages:claims.comments.newComment',
                    'New comment'
                  )}
                  accessorKey={key}
                />
              )}
            </Grid.Col>
          ))}
          {claimID && onSaveComment && (
            <Grid.Col span={12}>
              <Group justify="flex-end">
                <Button
                  variant="outline"
                  onClick={onSaveComment}
                  loading={isSavingComment}
                  disabled={!canSaveComment}
                >
                  {translate(
                    'pages:claims.actions.saveInternalComments',
                    'Save internal comments'
                  )}
                </Button>
              </Group>
            </Grid.Col>
          )}
        </Grid>
      </Stack>
    </Section>
  );

  return (
    <Card
      padding="md"
      shadow="xs"
      radius="md"
      withBorder
      data-testid="claim-note-section"
    >
      {collapsible ? (
        <Accordion
          value={expanded ? 'comments' : null}
          onChange={(value) => onExpandedChange?.(value === 'comments')}
          styles={{ item: { borderBottom: 0 } }}
        >
          <Accordion.Item value="comments">
            <Accordion.Control icon={<IconNotes size={20} />}>
              <Text fw={600}>{title}</Text>
            </Accordion.Control>
            <Accordion.Panel pt="md">{content}</Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      ) : (
        content
      )}
    </Card>
  );
};
